import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SuiToastService } from '@stride-ui/components';
import { AuthStore } from '../stores';
import { AuthService } from '../services';
import { API_ERROR_FALLBACK_KEY } from '../api-errors';

const REFRESH_ENDPOINT = 'auth/token';

@Injectable()
export class ErrorInterceptor<T> implements HttpInterceptor {
  private router = inject(Router);
  private authService = inject(AuthService);
  private authStore = inject(AuthStore);
  private translate = inject(TranslateService);
  private toastService = inject(SuiToastService);

  public intercept(
    request: HttpRequest<T>,
    next: HttpHandler,
  ): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpEvent<T>> => {
        // If not logged in, don't do anything
        if (!this.authStore.hasSession()) {
          return throwError(() => error);
        }

        if (error.status !== HttpStatusCode.Unauthorized) {
          return throwError(() => error.error);
        }

        if (!request.url.endsWith(REFRESH_ENDPOINT)) {
          return this.authService.refreshSession$().pipe(
            catchError((error: unknown) => {
              this.toastService.showErrorToast(
                this.translate.instant(API_ERROR_FALLBACK_KEY),
              );
              this.router.navigate(['/login']);
              return throwError(() => error);
            }),
            switchMap(() => {
              const session = this.authStore.getSession();
              const requestWithNewSession = request.clone({
                setHeaders: { Authorization: `Bearer ${session.accessToken}` },
              });
              return next
                .handle(requestWithNewSession)
                .pipe(catchError((error: unknown) => throwError(() => error)));
            }),
          );
        }

        // If refresh token is expired, remove session and redirect to login
        this.authStore.removeSession();
        this.router.navigate(['/login']);
        this.toastService.showErrorToast(
          this.translate.instant('auth.session_expired'),
        );
        return throwError(() => error);
      }),
    );
  }
}
